import React, { useMemo, useState } from 'react';
import { Search, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, X, Star, Crown } from 'lucide-react';
import { Card, CardContent } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { TicketListSkeleton } from './TicketSkeleton';
import { useTicketSearch } from './hooks/use-ticket-search';
import { useCategories } from '../../../hooks/useApi';
import { SupportTicket, SUPPORT_TIER_OPTIONS, PRIORITY_TIERS } from '../../../types';
import { NumberedPagination } from '@kioskgaming/ui';
import { useFormatDate } from '../../../hooks/useFormatDate';

type ListViewMode = 'all' | 'priority';

interface TicketListProps {
  onTicketSelect: (ticket: SupportTicket) => void;
}

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800', icon: MessageSquare },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  // Legacy status kept for display of old tickets only (not used in filters)
  done: { label: 'Closed', color: 'bg-gray-100 text-gray-800', icon: XCircle },
} as const;

// Statuses that should be available in the filter dropdown
const filterableStatuses: Array<keyof typeof statusConfig> = [
  'pending',
  'in_progress',
  'resolved',
  'closed',
];

const isPriorityTier = (tier: string | undefined): boolean =>
  !!tier && PRIORITY_TIERS.some((p) => tier.toLowerCase().includes(p.toLowerCase()));

const TicketList: React.FC<TicketListProps> = ({ onTicketSelect }) => {
  const fmt = useFormatDate();
  const [viewMode, setViewMode] = useState<ListViewMode>('all');
  const {
    tickets,
    pagination,
    isLoading,
    error,
    searchTerm,
    isSearchActive,
    statusFilter,
    tierFilter,
    offset,
    page,
    setSearchTerm,
    setStatusFilter,
    setTierFilter,
    nextPage,
    prevPage,
    setPage,
  } = useTicketSearch();
  const { data: categoriesData } = useCategories();

  const displayedTickets = useMemo(() => {
    if (viewMode !== 'priority') return tickets;
    return tickets.filter((t) => isPriorityTier(t.userTier));
  }, [tickets, viewMode]);

  const categoryDisplayMap = useMemo(() => {
    const raw = categoriesData?.data;
    const list = Array.isArray(raw)
      ? raw
      : (raw && typeof raw === 'object' && Array.isArray((raw as { categories?: unknown[] }).categories)
        ? (raw as { categories: { id: string; display_name: string }[] }).categories
        : []);
    const map = new Map<string, string>();
    list.forEach((c: { id: string; display_name: string }) => {
      map.set(c.id, c.display_name);
      map.set(c.display_name, c.display_name);
    });
    return map;
  }, [categoriesData?.data]);

  const getCategoryDisplayName = (ticket: SupportTicket): string =>
    ticket.categoryDisplayName || (categoryDisplayMap.get(ticket.category) ?? ticket.category ?? 'Unknown');

  return (
    <div className="space-y-6">
      {/* Header + View tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Support Tickets</h1>
          <div className="flex rounded-lg border bg-muted/40 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('all')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'all' ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setViewMode('priority')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === 'priority'
                  ? 'bg-amber-100 text-amber-900 shadow dark:bg-amber-900/40 dark:text-amber-200'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Platinum & Diamond tiers – prioritize these"
            >
              <Star className="h-4 w-4" />
              Priority
            </button>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {viewMode === 'priority'
            ? `${displayedTickets.length} priority ticket${displayedTickets.length !== 1 ? 's' : ''}`
            : `${pagination?.total || 0} ${isSearchActive ? 'results' : 'total tickets'}`}
        </div>
      </div>

      {/* Filters - always visible */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, phone or category name (min 3 chars)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {searchTerm && searchTerm.length < 3 && (
                <p className="text-xs text-muted-foreground mt-1">Type at least 3 characters to search</p>
              )}
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={statusFilter || 'all'}
                onChange={(e) => setStatusFilter(e.target.value === 'all' ? '' : e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
              >
                <option value="all">All</option>
                {filterableStatuses.map((status) => {
                  const config = statusConfig[status];
                  return (
                    <option key={status} value={status}>
                      {config.label}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Tier Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">Tier</label>
              <select
                value={tierFilter || 'all'}
                onChange={(e) => setTierFilter(e.target.value === 'all' ? '' : e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
              >
                <option value="all">All</option>
                {SUPPORT_TIER_OPTIONS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">Failed to load tickets</p>
        </div>
      )}

      {/* Tickets List - skeleton during loading */}
      {!error && (
        <div className="space-y-4">
          {isLoading ? (
            <TicketListSkeleton count={5} />
          ) : displayedTickets.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {viewMode === 'priority'
                    ? 'No priority (Platinum/Diamond) tickets in current results'
                    : isSearchActive
                      ? 'No tickets found for this search'
                      : 'No tickets found'}
                </p>
              </CardContent>
            </Card>
          ) : (
            displayedTickets.map((ticket) => {
              const statusInfo =
                statusConfig[ticket.status as keyof typeof statusConfig] || {
                  label: ticket.status,
                  color: 'bg-gray-100 text-gray-800',
                  icon: MessageSquare,
                };
              const StatusIcon = statusInfo.icon;
              const isPriority = isPriorityTier(ticket.userTier);
              return (
                <Card
                  key={ticket.id}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${isPriority ? 'ring-1 ring-amber-400/60 dark:ring-amber-500/40 bg-amber-50/50 dark:bg-amber-950/20' : ''}`}
                  onClick={() => onTicketSelect(ticket)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-semibold text-lg">{ticket.ticketNumber}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            <StatusIcon className="h-3 w-3 inline mr-1" />
                            {statusInfo.label}
                          </span>
                          {ticket.userTier && (
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                isPriority
                                  ? 'bg-amber-200 text-amber-900 dark:bg-amber-700/50 dark:text-amber-200'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {isPriority && <Crown className="h-3 w-3 inline mr-1" />}
                              {ticket.userTier}
                            </span>
                          )}
                        </div>

                        <div className="space-y-1 text-sm text-muted-foreground">
                          <p><strong>Email:</strong> {ticket.email}</p>
                          {ticket.phoneNumber && <p><strong>Phone:</strong> {ticket.phoneNumber}</p>}
                          <p>
                            <strong>Category:</strong>{' '}
                            {getCategoryDisplayName(ticket)}
                          </p>
                          <p><strong>Created:</strong> {fmt(ticket.createdAt)}</p>
                        </div>

                        <p className="mt-3 text-sm line-clamp-2">{ticket.message}</p>
                      </div>

                      <div className="ml-4 text-right text-sm text-muted-foreground">
                        {ticket.replies && ticket.replies.length > 0 && (
                          <div className="flex items-center gap-1 mb-2">
                            <MessageSquare className="h-4 w-4" />
                            <span>{ticket.replies.length} replies</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total > 0 && !isLoading && (
        <div className="flex justify-center items-center gap-4">
          {isSearchActive ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={offset === 0}
              >
                Previous
              </Button>

              <span className="text-sm text-muted-foreground">
                Showing {offset + 1}-{Math.min(offset + tickets.length, pagination.total)} of {pagination.total}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={offset + tickets.length >= pagination.total}
              >
                Next
              </Button>
            </>
          ) : (
            pagination.totalPages && pagination.totalPages > 1 && (
              <NumberedPagination
                currentPage={page}
                totalPages={pagination.totalPages}
                onPageChange={setPage}
                variant="admin"
              />
            )
          )}
        </div>
      )}
    </div>
  );
};

export default TicketList;
